import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/component/ds/utils';

/*
 * "Input - Vega" in Figma. Error state is driven by aria-invalid rather than a
 * prop, so it pairs with aria-describedby for the message.
 */
const inputVariants = cva(
  [
    'ds-flex ds-w-full ds-rounded-ds ds-bg-background',
    'ds-border ds-border-input ds-shadow-ds-2xs',
    'ds-text-foreground placeholder:ds-text-muted-foreground',
    'focus-visible:ds-outline-none focus-visible:ds-shadow-ds-focus',
    'disabled:ds-cursor-default disabled:ds-opacity-50',
    'aria-[invalid=true]:ds-border-destructive aria-[invalid=true]:ds-shadow-none',
    'aria-[invalid=true]:focus-visible:ds-shadow-ds-focus-error',
    'file:ds-border-0 file:ds-bg-transparent file:ds-text-ds-sm file:ds-font-medium'
  ],
  {
    variants: {
      size: {
        xs: 'ds-h-ds-xs ds-px-2 ds-py-[3px] ds-text-ds-mini',
        sm: 'ds-h-ds-sm ds-px-2 ds-py-1.5 ds-text-ds-sm',
        default: 'ds-h-ds-md ds-px-2.5 ds-py-2 ds-text-ds-sm',
        lg: 'ds-h-ds-lg ds-px-2.5 ds-py-2.5 ds-text-ds-base'
      }
    },
    defaultVariants: { size: 'default' }
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
  VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, size, type = 'text', ...props }, ref) => {
    return (
      <input
        ref={ref}
        type={type}
        className={cn(inputVariants({ size }), className)}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
